import React, { useState, useEffect, useRef } from 'react';
import { Brain, Send, Sparkles, Shield, Zap, TrendingUp, Info } from 'lucide-react';

const AIAssistant = () => {
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef(null);
  
  const [chat, setChat] = useState([
    { role: 'bot', message: 'Urban Intel Neural Network online. Telemetry synchronized. How can I assist your operations today?' }
  ]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chat, isTyping]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setChat(prev => [...prev, { role: 'user', message: input }]);
    setInput('');
    setIsTyping(true);
    setTimeout(() => {
      setChat(prev => [...prev, { role: 'bot', message: "Query processed. High-yield staking zones detected in MI Road sector." }]);
      setIsTyping(false);
    }, 1000);
  };

  return (
    <div className="ai-page-layout">
      {/* SIDEBAR - Clean & Simple */}
      <aside className="system-sidebar glass-panel">
        <div className="sidebar-brand">
          <Brain size={24} color="#8b5cf6" />
          <span>System Intel</span>
        </div>
        <div className="stats-list">
          <div className="stat-card"><TrendingUp size={16}/> Load: Optimal</div>
          <div className="stat-card"><Shield size={16}/> Secure v4.2</div>
          <div className="stat-card"><Zap size={16}/> 10k+ TPS</div>
        </div>
        <div className="sidebar-footer">
          <Info size={14} /> <span>Verify reports to earn MON.</span>
        </div>
      </aside>

      {/* MAIN CHAT AREA */}
      <main className="ai-main-chat">
        
        {/* TOP SECTION: Header + Query Box together */}
        <section className="chat-top-section glass-panel">
          <div className="intel-header">
            <div className="avatar-glow">
              <Brain size={28} color="#8b5cf6" />
            </div>
            <div className="header-text">
              <h2>Urban Intel AI</h2>
              <p className="status-text">Quantum Processing Active</p>
            </div>
            <Sparkles className="sparkle-icon" size={20} color="#8b5cf6" />
          </div>

          <form onSubmit={handleSend} className="query-box-container">
            <input 
              className="minimal-input"
              placeholder="Query the Urban Intelligence Network..." 
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <button type="submit" className="minimal-send">
              <Send size={18} />
            </button>
          </form>
        </section>

        {/* BOTTOM SECTION: Message Feed */}
        <section className="message-feed" ref={scrollRef}>
          {chat.map((msg, i) => (
            <div key={i} className={`chat-bubble ${msg.role}`}>
              {msg.message}
            </div>
          ))}
          {isTyping && <div className="chat-bubble bot typing">...</div>}
        </section>

      </main>

      <style>{`
        .ai-page-layout {
          display: flex;
          gap: 20px;
          height: calc(100vh - 90px);
          padding: 20px;
          margin-top: 75px;
          max-width: 1100px;
          margin-inline: auto;
          color: white;
        }

        .glass-panel {
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 20px;
        }

        /* SIDEBAR */
        .system-sidebar { width: 220px; padding: 20px; display: flex; flex-direction: column; gap: 20px; flex-shrink: 0; }
        .sidebar-brand { display: flex; align-items: center; gap: 10px; font-weight: bold; border-bottom: 1px solid rgba(255,255,255,0.05); padding-bottom: 15px; }
        .stats-list { display: flex; flex-direction: column; gap: 10px; }
        .stat-card { background: rgba(255,255,255,0.03); padding: 10px; border-radius: 10px; font-size: 0.8rem; display: flex; gap: 8px; align-items: center; color: #94a3b8; }
        .sidebar-footer { margin-top: auto; font-size: 0.75rem; color: #64748b; display: flex; gap: 6px; }

        /* MAIN CHAT AREA */
        .ai-main-chat { flex: 1; display: flex; flex-direction: column; gap: 20px; }

        /* TOP SECTION (Header + Input) */
        .chat-top-section { padding: 24px; display: flex; flex-direction: column; gap: 20px; border: 1px solid rgba(139, 92, 246, 0.2); }
        .intel-header { display: flex; align-items: center; gap: 15px; }
        .avatar-glow { background: rgba(139, 92, 246, 0.1); padding: 10px; border-radius: 12px; position: relative; }
        .header-text h2 { margin: 0; font-size: 1.2rem; font-weight: 700; }
        .status-text { margin: 0; font-size: 0.75rem; color: #10b981; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .sparkle-icon { margin-left: auto; opacity: 0.6; }

        .query-box-container { display: flex; gap: 10px; background: rgba(0,0,0,0.2); border: 1px solid rgba(255,255,255,0.1); padding: 6px; border-radius: 12px; }
        .minimal-input { flex: 1; background: transparent; border: none; color: white; padding: 10px; outline: none; font-size: 0.95rem; }
        .minimal-send { background: #8b5cf6; color: white; border: none; width: 38px; height: 38px; border-radius: 8px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.2s; }
        .minimal-send:hover { transform: scale(1.05); background: #7c3aed; }

        /* MESSAGE FEED */
        .message-feed { flex: 1; overflow-y: auto; padding: 10px; display: flex; flex-direction: column; gap: 12px; }
        .chat-bubble { max-width: 80%; padding: 12px 16px; border-radius: 16px; font-size: 0.9rem; line-height: 1.4; }
        .chat-bubble.bot { align-self: flex-start; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.05); }
        .chat-bubble.user { align-self: flex-end; background: linear-gradient(135deg, #3b82f6, #8b5cf6); color: white; }
        .typing { font-style: italic; opacity: 0.6; }

        .message-feed::-webkit-scrollbar { width: 4px; }
        .message-feed::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
      `}</style>
    </div>
  );
};

export default AIAssistant;